/**
 * Utility to detect if styled-components features are being used
 * This helps us decide whether to use Tailwind or styled-components rendering
 */

// List of all styled-system props that require styled-components
const STYLED_SYSTEM_PROPS = [
  // Common props
  "bg",
  "backgroundColor",
  "color",
  "m",
  "margin",
  "mt",
  "mr",
  "mb",
  "ml",
  "mx",
  "my",
  "p",
  "padding",
  "pt",
  "pr",
  "pb",
  "pl",
  "px",
  "py",
  // Layout props
  "width",
  "height",
  "minWidth",
  "maxWidth",
  "minHeight",
  "maxHeight",
  "display",
  "verticalAlign",
  "overflow",
  "overflowX",
  "overflowY",
  // Flexbox props
  "alignItems",
  "alignContent",
  "justifyItems",
  "justifyContent",
  "flexWrap",
  "flexDirection",
  "flex",
  "flexGrow",
  "flexShrink",
  "flexBasis",
  "justifySelf",
  "alignSelf",
  "order",
  // Position props — keep in sync with the POSITION composition in system-props.ts
  "position",
  "zIndex",
  "top",
  "right",
  "bottom",
  "left",
  // Grid props
  "gridGap",
  "gridRowGap",
  "gridColumnGap",
  "gridColumn",
  "gridRow",
  "gridArea",
  "gridAutoFlow",
  "gridAutoRows",
  "gridAutoColumns",
  "gridTemplateRows",
  "gridTemplateColumns",
  "gridTemplateAreas",
  // Border props — keep in sync with the BORDER composition in system-props.ts
  "border",
  "borderTop",
  "borderRight",
  "borderBottom",
  "borderLeft",
  "borderWidth",
  "borderStyle",
  "borderColor",
  "borderRadius",
  "boxShadow",
  // Typography props — keep in sync with the TYPOGRAPHY composition in system-props.ts
  "typeScale",
  "fontFamily",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "lineHeight",
  "letterSpacing",
  "textAlign",
] as const;

/**
 * Matches the generated `sc-XXXX` class that styled-components injects onto the
 * className of a `styled(Component)` extension.
 */
const SC_CLASS_PATTERN = /\bsc-[a-zA-Z0-9]+\b/;

/**
 * Detect if any styled-components props are present
 * Returns true if the component needs styled-components, false if Tailwind can be used
 */
export function hasStyledProps(props: Record<string, any>): boolean {
  return STYLED_SYSTEM_PROPS.some((prop) => props[prop] !== undefined);
}

// BORDER-mixin props (see system-props.ts `BORDER`). These are NOT part of
// STYLED_SYSTEM_PROPS but still require the styled-components path when present,
// because the BORDER mixin only resolves through styled-system / the theme.
const BORDER_PROPS = [
  "border",
  "borderTop",
  "borderRight",
  "borderBottom",
  "borderLeft",
  "borderWidth",
  "borderStyle",
  "borderColor",
  "borderRadius",
  "boxShadow",
] as const;

/**
 * Detect if any BORDER-mixin props are present.
 * Additive helper for hybrids (e.g. Avatar) whose styled container applies the
 * BORDER mixin; lets the hybrid route border props to the styled-components path.
 */
export function hasBorderProps(props: Record<string, any>): boolean {
  return BORDER_PROPS.some((prop) => props[prop] !== undefined);
}

/**
 * Detect if a component was extended with styled() from styled-components.
 * When styled() wraps a component it injects a `theme` prop via context.
 * It also sets `forwardedComponent` in some configurations, and always injects
 * a generated `sc-XXXX` class onto `className` — the most reliable signal when
 * no ThemeProvider is present.
 * Returns true if the styled-components rendering path should be used.
 */
export function isStyledExtension(props: Record<string, any>): boolean {
  const hasScClass =
    typeof props.className === "string" &&
    SC_CLASS_PATTERN.test(props.className);
  return !!props.forwardedComponent || !!props.theme || hasScClass;
}

/**
 * Combined check — returns true if styled-components rendering is needed for any reason.
 * Use this as the single gate in hybrid components.
 */
export function needsStyledComponents(props: Record<string, any>): boolean {
  return hasStyledProps(props) || isStyledExtension(props);
}
